import { useState, useEffect } from 'react'
import { SiteOption } from '../../../types/SiteOption'
import { MarginTemplate, MarginState } from '../../../types/MarginValuation'
import { useSites } from '../../../hooks/useSites'
import { buildSaveTargetQuery, SaveTarget } from '../moduleVoNavigation'
import { buildGlobalConfig, sanitizeMargin, toDisplay, toStored } from '../utils/marginValuationUtils'
import BackOfficeAPIClient from '../../../clients/BackofficeAPIClient'

const backofficeApiClient = new BackOfficeAPIClient()

interface UseMarginValuationReturn {
    sites: SiteOption[]
    globalConfig: MarginState | null
    setGlobalConfig: (config: MarginState) => void
    siteConfig: MarginState | null
    setSiteConfig: (config: MarginState) => void
    selectedSite: SiteOption | null
    setSelectedSite: (site: SiteOption | null) => void
    hasChanges: boolean
    submitting: boolean
    loading: boolean
    isSiteMode: boolean
    siteIsModified: boolean
    updateGlobal: (field: 'margin' | 'isActive', value: string | boolean) => void
    updateSite: (field: 'margin' | 'isActive', value: string | boolean) => void
    save: (target: SaveTarget, value: MarginState | null) => Promise<void>
}

/**
 * Ne fetch réellement que le global et, si un site est sélectionné, ce site (globalConfig et
 * siteConfig séparés, pour l'affichage à deux lignes Global/Site). Les autres portées (marque
 * seule, plusieurs sites/marques, ou une combinaison site+marque) sont calculées côté composant
 * (MarginValuation.tsx) à partir de rawData (useMarginValuationRawData) — ce hook ne les connaît
 * pas, il se contente d'exposer `globalConfig`/`setGlobalConfig` pour qu'il puisse y écrire.
 */
export function useMarginValuation(): UseMarginValuationReturn {
    const sites = useSites()
    const [globalConfig, setGlobalConfig] = useState<MarginState | null>(null)
    const [siteConfig, setSiteConfig] = useState<MarginState | null>(null)
    const [selectedSite, setSelectedSite] = useState<SiteOption | null>(null)
    const [hasChanges, setHasChanges] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [loading, setLoading] = useState(true)

    const selectedSiteId = selectedSite?.value ?? null
    const isSiteMode = selectedSite !== null
    const siteIsModified = siteConfig !== null && (
        siteConfig.margin !== siteConfig.originalMargin || siteConfig.isActive !== siteConfig.originalIsActive
    )

    useEffect(() => {
        fetchConfig(selectedSiteId)
    }, [selectedSiteId])

    const fetchConfig = async (siteId: number | null) => {
        setLoading(true)
        try {
            if (siteId !== null) {
                const [globalData, siteData] = await Promise.all([
                    backofficeApiClient.get<MarginTemplate | null>('/valuation-settings'),
                    backofficeApiClient.get<MarginTemplate | null>(`/valuation-settings?dealership_id=${siteId}`),
                ])

                setGlobalConfig(buildGlobalConfig(globalData))

                if (siteData) {
                    const siteMargin = toDisplay(siteData.margin)
                    setSiteConfig({ id: siteData.id, margin: siteMargin, isActive: siteData.is_active, originalMargin: siteMargin, originalIsActive: siteData.is_active })
                } else {
                    setSiteConfig(null)
                }
            } else {
                const data = await backofficeApiClient.get<MarginTemplate | null>('/valuation-settings')
                setGlobalConfig(buildGlobalConfig(data))
                setSiteConfig(null)
            }

            setHasChanges(false)
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const updateGlobal = (field: 'margin' | 'isActive', value: string | boolean) => {
        if (field === 'margin') value = sanitizeMargin(value as string)
        setGlobalConfig(prev => prev ? { ...prev, [field]: value } : prev)
        setHasChanges(true)
    }

    // Si le site n'a pas encore sa propre ligne, la première modification en crée une "à la volée"
    // en partant du global comme base (plutôt que de démarrer à zéro).
    const updateSite = (field: 'margin' | 'isActive', value: string | boolean) => {
        if (field === 'margin') value = sanitizeMargin(value as string)
        setSiteConfig(prev => {
            if (prev) return { ...prev, [field]: value }
            return {
                margin: globalConfig?.margin ?? '0',
                isActive: field === 'isActive' ? value as boolean : true,
                originalMargin: globalConfig?.margin ?? '0',
                originalIsActive: globalConfig?.isActive ?? true,
                [field]: value,
            }
        })
        setHasChanges(true)
    }

    const save = async (target: SaveTarget, value: MarginState | null) => {
        setSubmitting(true)
        try {
            if (value) {
                // Pas d'id dans le payload : une seule valeur peut viser plusieurs sites/marques
                // (target), chacun avec sa propre ligne (existante ou non) en base. C'est à l'API
                // de faire l'upsert par cible, pas au front de désigner une ligne précise.
                const query = buildSaveTargetQuery(target)
                await backofficeApiClient.put(`/valuation-settings${query}`, {
                    margin: toStored(value.margin),
                    is_active: value.isActive,
                })
            }

            if (target.dealershipIds.length === 1 && target.brands.length === 0) {
                await fetchConfig(target.dealershipIds[0])
            } else if (target.dealershipIds.length === 0 && target.brands.length === 0) {
                await fetchConfig(null)
            } else {
                // Cibles multi-sites, multi-marques ou marque seule : pas de rechargement dédié
                // (la valeur affichée vient de la fusion calculée par le composant), mais il faut
                // quand même sortir de l'état "modifications non sauvegardées".
                setHasChanges(false)
            }
        } catch (error) {
            console.log(error)
        } finally {
            setSubmitting(false)
        }
    }

    return {
        sites,
        globalConfig,
        setGlobalConfig,
        siteConfig,
        setSiteConfig,
        selectedSite,
        setSelectedSite,
        hasChanges,
        submitting,
        loading,
        isSiteMode,
        siteIsModified,
        updateGlobal,
        updateSite,
        save,
    }
}
